import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, trackPageview } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { openSubscribe } from "../components/SubscribeModalHost";
import { LangContext } from "../lib/LangContext";
import { useSeoTags, useJsonLd } from "../lib/useSeoTags";
import { t as translate } from "../lib/i18n";
import { ExternalLink } from "lucide-react";

const SITE = "https://islandsdaily.com";
const LANG_PREFIX = { en: "story", nl: "verhaal", es: "historia" };

// Related SEO landing pages per island × lang (used at the bottom of
// each story). Only the pages we already ship — no new content.
const RELATED_BY_ISLAND_LANG = {
  aruba: {
    en: [
      { href: "/aruba", label: "All Aruba news" },
      { href: "/aruba-news-english", label: "Aruba News in English" },
      { href: "/aruba-daily-briefing", label: "Aruba Daily Briefing" },
    ],
    nl: [
      { href: "/aruba", label: "Alle Aruba-nieuws" },
      { href: "/nieuws-aruba", label: "Aruba-nieuws in het Nederlands" },
      { href: "/aruba-dagelijkse-briefing", label: "Aruba Dagelijkse Briefing" },
    ],
    es: [
      { href: "/aruba", label: "Todas las noticias de Aruba" },
      { href: "/noticias-aruba", label: "Noticias de Aruba en español" },
      { href: "/resumen-diario-aruba", label: "Resumen Diario de Aruba" },
    ],
  },
  curacao: {
    en: [
      { href: "/curacao", label: "All Curaçao news" },
      { href: "/curacao-news-english", label: "Curaçao News in English" },
      { href: "/caribbean-morning-briefing", label: "Caribbean Morning Briefing" },
    ],
    nl: [
      { href: "/curacao", label: "Alle Curaçao-nieuws" },
      { href: "/nieuws-curacao", label: "Curaçao-nieuws in het Nederlands" },
      { href: "/caribisch-ochtend-briefing", label: "Caribische Ochtendbriefing" },
    ],
    es: [
      { href: "/curacao", label: "Todas las noticias de Curazao" },
      { href: "/noticias-curazao", label: "Noticias de Curazao en español" },
      { href: "/resumen-matutino-caribe", label: "Resumen Matutino del Caribe" },
    ],
  },
};

const READ_ORIGINAL = {
  en: "Read the original at",
  nl: "Lees het origineel op",
  es: "Lea el original en",
};

const HEADER_TAGLINE = {
  en: "Today's top story",
  nl: "Verhaal van de dag",
  es: "Historia del día",
};

const RELATED_LABEL = {
  en: "Related",
  nl: "Gerelateerd",
  es: "Relacionado",
};

// Extract 24-char hex article id from the URL slug parameter.
const parseArticleId = (idSlug) => {
  if (!idSlug) return "";
  const head = idSlug.split("-", 1)[0];
  return head.length >= 20 ? head : "";
};

export default function StoryPage({ lang }) {
  const { idSlug } = useParams();
  const articleId = parseArticleId(idSlug);
  const [story, setStory] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!articleId) { setNotFound(true); return; }
    trackPageview(`/${LANG_PREFIX[lang]}/${idSlug}`);
    api
      .get(`/top-stories/story/${articleId}`, { params: { lang } })
      .then((r) => {
        if (r.data && r.data.found) setStory(r.data);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [articleId, idSlug, lang]);

  const seoConfigs = useMemo(() => {
    if (!story) return {};
    const title = story.titles?.[lang] || story.titles?.en || "";
    const summary = story.summaries?.[lang] || story.summaries?.en || "";
    const slugs = story.slugs || {};
    const canonical = `${SITE}/${LANG_PREFIX[lang]}/${articleId}-${slugs[lang] || slugs.en || ""}`;
    const alternates = [
      ...["en", "nl", "es"].map((lc) => ({
        hreflang: lc,
        href: `${SITE}/${LANG_PREFIX[lc]}/${articleId}-${slugs[lc] || slugs.en || ""}`,
      })),
      { hreflang: "x-default",
        href: `${SITE}/story/${articleId}-${slugs.en || ""}` },
    ];
    return {
      [lang]: {
        title: `${title} — IslandsDaily`,
        description: summary.slice(0, 200),
        canonical,
        alternates,
      },
    };
  }, [story, lang, articleId]);
  useSeoTags(lang, seoConfigs);

  // NewsArticle JSON-LD — targets Google's Top Stories carousel eligibility.
  // Author + Publisher are both IslandsDaily (aggregator model — Google allows
  // this). headline capped at 110 chars per Google's News guidelines.
  const newsArticleLd = useMemo(() => {
    if (!story) return null;
    const title = story.titles?.[lang] || story.titles?.en || "";
    const summary = story.summaries?.[lang] || story.summaries?.en || "";
    const slugs = story.slugs || {};
    const canonical = `${SITE}/${LANG_PREFIX[lang]}/${articleId}-${slugs[lang] || slugs.en || ""}`;
    const pub = story.published_at || new Date().toISOString();
    const publisher = {
      "@type": "Organization",
      name: "IslandsDaily",
      url: SITE,
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/logo512.png`,
        width: 512,
        height: 512,
      },
    };
    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: title.slice(0, 110),
      description: summary.slice(0, 300),
      image: story.image_url ? [story.image_url] : undefined,
      datePublished: pub,
      dateModified: pub,
      author: publisher,
      publisher,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonical,
      },
      articleSection: story.category || undefined,
      inLanguage: lang,
      isAccessibleForFree: true,
    };
  }, [story, lang, articleId]);
  useJsonLd(`news-article-${articleId}-${lang}`, newsArticleLd);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white p-8" data-testid="story-page-404">
        <p>{translate(lang, "error_story_not_found")}</p>
        <Link to="/" className="text-[#0AADA0]">← Home</Link>
      </div>
    );
  }
  if (!story) {
    return (
      <div className="min-h-screen bg-white p-8" data-testid="story-page-loading">
        <p className="text-[#6B7280]">Loading…</p>
      </div>
    );
  }

  const title = story.titles?.[lang] || story.titles?.en || "";
  const summary = story.summaries?.[lang] || story.summaries?.en || "";
  const related = RELATED_BY_ISLAND_LANG[story.island]?.[lang] || [];

  return (
    <LangContext.Provider value={lang}>
      <div className="min-h-screen bg-white text-[#1A1A1A]"
           data-testid={`story-page-${articleId}`}>
        <Header
          category="All"
          onCategoryChange={() => {}}
          island="all"
          onIslandChange={() => {}}
          lang={lang}
          onLangChange={() => {}}
          onSubscribe={() => openSubscribe("story_page")}
        />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-[11px] uppercase tracking-wider text-[#0AADA0] mb-2"
               data-testid="story-tagline">
            {HEADER_TAGLINE[lang]}
          </div>
          {story.image_url && (
            <img
              src={story.image_url}
              alt={title}
              className="w-full rounded-lg mb-6 aspect-video object-cover"
              data-testid="story-image"
            />
          )}
          <div className="mb-3">
            <span className="inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider bg-[#F5F5F5] text-[#1A2E35] rounded"
                  data-testid="story-category">
              {story.category}
            </span>
          </div>
          <h1
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1A2E35] mb-6"
            data-testid="story-h1"
          >
            {title}
          </h1>
          <p className="text-[16px] text-[#374151] leading-relaxed mb-8 whitespace-pre-line"
             data-testid="story-summary">
            {summary}
          </p>

          {story.source_url && (
            <a
              href={story.source_url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1.5 text-[14px] text-[#0AADA0] hover:underline mb-10"
              data-testid="story-source-link"
            >
              {READ_ORIGINAL[lang]} {story.source_name}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}

          {related.length > 0 && (
            <section className="mt-12 pt-8 border-t border-[#EEEEEE]"
                     data-testid="story-related">
              <h2 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">
                {RELATED_LABEL[lang]}
              </h2>
              <ul className="space-y-2">
                {related.map((l, i) => (
                  <li key={i}>
                    <Link
                      to={l.href}
                      className="text-[14px] text-[#0AADA0] hover:underline"
                      data-testid={`story-related-link-${i}`}
                    >
                      {l.label} →
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </main>

        <Footer onSubscribe={() => openSubscribe("story_page_footer")} />
      </div>
    </LangContext.Provider>
  );
}
